import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { API_ENDPOINTS } from '../utils/config';
import '../styles/AddressModal.css';

interface Address {
  _id: string;
  title: string;
  address: string;
  is_default?: boolean;
  created_at: string;
}

interface AddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddressSelected?: (address: string) => void;
  isSelectionMode?: boolean;
}

const AddressModal: React.FC<AddressModalProps> = ({ 
  isOpen, 
  onClose, 
  onAddressSelected,
  isSelectionMode = false 
}) => {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(false);
  const [newAddress, setNewAddress] = useState({ title: '', address: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchAddresses();
    }
  }, [isOpen]);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      if (!user?.company_id) {
        setError('User is not associated with a company');
        return;
      }

      const response = await fetch(API_ENDPOINTS.COMPANY_ADDRESSES(user.company_id), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAddresses(result.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAddress.title.trim() || !newAddress.address.trim()) {
      setError(t('common.please_fill_all_fields'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (!user?.company_id) {
        setError('User is not associated with a company');
        return;
      }

      const response = await fetch(API_ENDPOINTS.COMPANY_ADDRESSES(user.company_id), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAddress)
      });

      const result = await response.json();
      
      if (result.success) {
        setSuccess(t('common.address_added_successfully'));
        setNewAddress({ title: '', address: '' });
        fetchAddresses();
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || t('common.error_adding_address'));
      }
    } catch (error) {
      setError(t('common.error_adding_address'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddressSelect = (address: Address) => {
    if (isSelectionMode && onAddressSelected) {
      onAddressSelected(address.address);
      onClose();
    }
  };

  const handleDeleteAddress = async (addressId: string) => {
    if (!window.confirm(t('common.confirm_delete_address'))) {
      return;
    }

    try {
      if (!user?.company_id) {
        setError('User is not associated with a company');
        return;
      }

      const response = await fetch(API_ENDPOINTS.COMPANY_ADDRESS(user.company_id, addressId), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setSuccess(t('common.address_deleted_successfully'));
        fetchAddresses();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || t('common.error_deleting_address'));
      }
    } catch (error) {
      setError(t('common.error_deleting_address'));
    }
  };

  const handleSetDefault = async (addressId: string) => {
    try {
      if (!user?.company_id) {
        setError('User is not associated with a company');
        return;
      }

      const response = await fetch(API_ENDPOINTS.COMPANY_ADDRESS_DEFAULT(user.company_id, addressId), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setSuccess(t('common.default_address_updated'));
        fetchAddresses();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || t('common.error_updating_default'));
      }
    } catch (error) {
      setError(t('common.error_updating_default'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="address-modal-overlay" onClick={onClose}>
      <div className="address-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="address-modal-header">
          <h2>{isSelectionMode ? t('common.select_address') : t('common.manage_addresses')}</h2>
          <button className="close-button" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {error && (
          <div className="error-message">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {success}
          </div>
        )}

        <div className="address-modal-body">
          {/* Add New Address Form */}
          {!isSelectionMode && (
            <div className="add-address-section">
              <h3>{t('common.add_new_address')}</h3>
              <form onSubmit={handleSubmit} className="add-address-form">
                <div className="form-group">
                  <label>{t('common.address_title')}</label>
                  <input
                    type="text"
                    value={newAddress.title}
                    onChange={(e) => setNewAddress(prev => ({ ...prev, title: e.target.value }))}
                    placeholder={t('common.enter_address_title')}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>{t('common.address')}</label>
                  <textarea
                    value={newAddress.address}
                    onChange={(e) => setNewAddress(prev => ({ ...prev, address: e.target.value }))}
                    placeholder={t('common.enter_full_address')}
                    className="form-textarea"
                    rows={3}
                  />
                </div>
                <button 
                  type="submit" 
                  className="add-address-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('common.adding') : t('common.add_address')}
                </button>
              </form>
            </div>
          )}

          {/* Addresses List */}
          <div className="addresses-section">
            <h3>{t('common.saved_addresses')}</h3>
            {loading ? (
              <div className="loading-spinner">{t('common.loading')}</div>
            ) : addresses.length === 0 ? (
              <div className="no-addresses">
                <p>{t('common.no_addresses_found')}</p>
              </div>
            ) : (
              <div className="addresses-list">
                {addresses.map((address) => (
                  <div 
                    key={address._id} 
                    className={`address-item ${isSelectionMode ? 'selectable' : ''} ${address.is_default ? 'default' : ''}`}
                    onClick={() => handleAddressSelect(address)}
                  >
                    <div className="address-content">
                      <div className="address-header">
                        <h4>{address.title}</h4>
                        {address.is_default && (
                          <span className="default-badge">{t('common.default')}</span>
                        )}
                      </div>
                      <p className="address-text">{address.address}</p>
                    </div>
                    {!isSelectionMode && (
                      <div className="address-actions">
                        {!address.is_default && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetDefault(address._id);
                            }}
                            className="action-btn set-default-btn"
                            title={t('common.set_as_default')}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAddress(address._id);
                          }}
                          className="action-btn delete-btn"
                          title={t('common.delete_address')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6H5H21M8 6V4A2 2 0 0 1 10 2H14A2 2 0 0 1 16 4V6M19 6V20A2 2 0 0 1 17 22H7A2 2 0 0 1 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {isSelectionMode && (
          <div className="address-modal-footer">
            <button className="cancel-btn" onClick={onClose}>
              {t('common.cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressModal;

